import { useEffect, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Combatant, CombatantStats, FullState, ServerMessage, TempEffect } from '@sd/shared';
import { AppHeader } from '../components/AppHeader.js';
import { useGm } from '../hooks/useGm.js';
import { getGmSecret } from '../lib/gm.js';
import { EncounterConnection } from '../lib/ws.js';
import { useTheme, themeClass } from '../hooks/useTheme.js';
import { PipBar, nextMarks } from '../components/combat/PipBar.js';
import { CONDITIONS } from '../components/combat/conditions.js';
import { SourcePicker } from '../components/combat/SourcePicker.js';
import { Icon } from '../components/adventures/Icon.js';
import { iconTypeFor } from '../components/adventures/icons.js';
import { D20Roller, type D20RollResult } from '../components/D20Roller.js';
import { DiceRoller } from '../components/DiceRoller.js';
import '../styles/encounter.css';
import '../styles/combat.css';
import '../styles/tracker.css';

function genId(prefix: string): string {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
}

/**
 * Бойовий трекер — спрощена сторінка без мапи. Герої + противники списком,
 * з інлайн-редагуванням статів, ефектами (conditions) і тимчасовими
 * примітками (tempEffects). Працює над тією ж encounter через GM WS, тож
 * синхронізується з мапою / гравцями.
 */
export function BattleTracker(): JSX.Element {
  const { id = '' } = useParams<{ id: string }>();
  const { isGm } = useGm();
  const { theme } = useTheme();
  const [state, setState] = useState<FullState | null>(null);
  const [conn, setConn] = useState<'connecting' | 'open' | 'closed' | 'error'>('connecting');
  const [showAddHero, setShowAddHero] = useState(false);
  const [showPicker, setShowPicker] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const wsRef = useRef<EncounterConnection | null>(null);

  // Dice-config для кидків з розгорнутої картки (атака d20 / playback).
  type DiceConfig =
    | { kind: 'duality'; modifier: number; label?: string; autoRoll: boolean;
        playback?: { hope: number; fear: number; advDie?: number; modifier?: number } }
    | { kind: 'd20'; modifier: number; label?: string; autoRoll: boolean;
        playback?: { dice: number[]; pickedIndex: number; modifier?: number } };
  const [diceConfig, setDiceConfig] = useState<DiceConfig | null>(null);
  const lastDiceLogAtRef = useRef<number>(0);

  useEffect(() => {
    if (!isGm) return;
    const secret = getGmSecret();
    if (!secret) return;
    const c = new EncounterConnection({
      encounterId: id,
      role: 'gm',
      credential: secret,
      onMessage: (m: ServerMessage) => {
        if (m.type === 'state:full' || m.type === 'state:patch') setState(m.payload as FullState);
      },
      onStatus: setConn,
    });
    wsRef.current = c;
    return () => c.close();
  }, [id, isGm]);

  // Підписка на нові roll-логи → playback dice-модалки (синхронно з мапою/гравцями).
  useEffect(() => {
    if (!state) return;
    const log = state.encounter.log;
    if (!log.length) return;
    for (const e of log.filter((x) => x.at > lastDiceLogAtRef.current)) {
      if (e.kind !== 'roll' || !e.roll || diceConfig?.autoRoll) continue;
      const r = e.roll;
      const label = r.label ?? (e.who ?? 'Roll');
      if (r.kind === 'd20' && r.dice && r.pickedIndex !== undefined) {
        setDiceConfig({ kind: 'd20', modifier: r.modifier ?? 0, label, autoRoll: false,
          playback: { dice: r.dice, pickedIndex: r.pickedIndex, modifier: r.modifier } });
      } else if (r.hope !== undefined && r.fear !== undefined) {
        setDiceConfig({ kind: 'duality', modifier: r.modifier ?? 0, label, autoRoll: false,
          playback: { hope: r.hope, fear: r.fear, advDie: r.advDie, modifier: r.modifier } });
      }
    }
    const maxAt = log[log.length - 1]?.at ?? 0;
    if (maxAt > lastDiceLogAtRef.current) lastDiceLogAtRef.current = maxAt;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  if (!isGm) {
    return (
      <div className="encounter-shell">
        <AppHeader />
        <div className="encounter-body">
          <h1 style={{ fontFamily: 'Cormorant Garamond, serif', color: 'var(--gold-bright)' }}>GM only</h1>
          <p style={{ color: 'var(--text-muted)' }}>
            Відкрий <code>/gm/&lt;твій-секрет&gt;</code>.
          </p>
        </div>
      </div>
    );
  }

  const ws = wsRef.current;
  const send = (msg: Parameters<NonNullable<typeof ws>['send']>[0]): void => { ws?.send(msg); };

  const upsertStats = (c: Combatant, patch: Partial<CombatantStats>): void => {
    send({ type: 'combatant:upsert', payload: { id: c.id, stats: { ...c.stats, ...patch } } });
  };
  const setMark = (c: Combatant, field: 'hp' | 'stress' | 'armor', value: number): void => {
    const max = field === 'hp' ? c.stats.hpMax : field === 'stress' ? c.stats.stressMax : c.stats.armorMax;
    upsertStats(c, { [field]: Math.max(0, Math.min(max, value)) });
  };
  const setMaxField = (c: Combatant, field: 'hpMax' | 'stressMax' | 'armorMax' | 'difficulty' | 'attackMod', value: number): void => {
    upsertStats(c, { [field]: value });
  };
  const toggleCondition = (c: Combatant, type: string, on: boolean): void => {
    send({ type: 'combatant:condition', payload: { id: c.id, condition: type as Combatant['conditions'][number], on } });
  };
  const addTempEffect = (c: Combatant, text: string): void => {
    const t = text.trim();
    if (!t) return;
    const list: TempEffect[] = [...(c.stats.tempEffects ?? []), { id: genId('te'), text: t }];
    upsertStats(c, { tempEffects: list });
  };
  const removeTempEffect = (c: Combatant, effId: string): void => {
    const list = (c.stats.tempEffects ?? []).filter((e) => e.id !== effId);
    upsertStats(c, { tempEffects: list });
  };
  const deleteCombatant = (c: Combatant): void => {
    if (!window.confirm(`Прибрати «${c.name}» з бою?`)) return;
    send({ type: 'combatant:delete', payload: { id: c.id } });
  };
  const attackRoll = (c: Combatant, label: string): void => {
    setDiceConfig({ kind: 'd20', modifier: c.stats.attackMod ?? 0, label, autoRoll: true });
  };
  const damageRoll = (c: Combatant, formula: string, label: string, damageType?: string): void => {
    send({ type: 'damage:roll', payload: { formula, label, damageType, fromId: c.id } });
  };

  const combatants = state?.combatants ?? [];
  const heroes = combatants.filter((c) => c.kind === 'player' || c.kind === 'ally');
  const foes = combatants.filter((c) => c.kind === 'adversary');
  const heroCount = combatants.filter((c) => c.kind === 'player').length;

  return (
    <div className="encounter-shell">
      <AppHeader />

      <div className={`tracker-page ${themeClass(theme)}`.trim()}>
        <div className="tracker-toolbar">
          <div className="tracker-title">
            <h1>{state?.encounter.name ?? 'Бойовий трекер'}</h1>
            <span className={`conn-pill ${conn}`}>{conn}</span>
          </div>
          <div className="tracker-toolbar-actions">
            <TrackFear
              value={state?.encounter.fear ?? 0}
              onAdjust={(d) => send({ type: 'fear:adjust', payload: { delta: d } })}
            />
            <Link to={`/encounter/${encodeURIComponent(id)}`} className="tracker-link">Мапа →</Link>
          </div>
        </div>

        <BattlePoints foes={foes} defaultPcs={heroCount || 4} />

        <div className="tracker-columns">
          {/* Героï */}
          <section className="tracker-col">
            <header className="tracker-col-head heroes">
              <h2>Герої</h2>
              <button type="button" className="tracker-add" onClick={() => setShowAddHero(true)}>+ Герой</button>
            </header>
            {heroes.length === 0 && <p className="tracker-empty">Ще нема героїв. Додай вручну.</p>}
            {heroes.map((c) => (
              <TrackerRow
                key={c.id}
                c={c}
                expanded={expandedId === c.id}
                onToggleExpand={() => setExpandedId(expandedId === c.id ? null : c.id)}
                onMark={setMark}
                onMax={setMaxField}
                onCondition={toggleCondition}
                onAddEffect={addTempEffect}
                onRemoveEffect={removeTempEffect}
                onDelete={deleteCombatant}
                onAttackRoll={attackRoll}
                onDamageRoll={damageRoll}
              />
            ))}
          </section>

          {/* Противники */}
          <section className="tracker-col">
            <header className="tracker-col-head foes">
              <h2>Противники</h2>
              <button type="button" className="tracker-add" onClick={() => setShowPicker(true)}>+ Противник</button>
            </header>
            {foes.length === 0 && <p className="tracker-empty">Ще нема противників. Обери з джерел.</p>}
            {foes.map((c) => (
              <TrackerRow
                key={c.id}
                c={c}
                expanded={expandedId === c.id}
                onToggleExpand={() => setExpandedId(expandedId === c.id ? null : c.id)}
                onMark={setMark}
                onMax={setMaxField}
                onCondition={toggleCondition}
                onAddEffect={addTempEffect}
                onRemoveEffect={removeTempEffect}
                onDelete={deleteCombatant}
                onAttackRoll={attackRoll}
                onDamageRoll={damageRoll}
              />
            ))}
          </section>
        </div>
      </div>

      {showAddHero && (
        <AddHeroModal
          onSubmit={(payload) => {
            send({ type: 'combatant:upsert', payload: { ...payload, encounterId: id } });
            setShowAddHero(false);
          }}
          onCancel={() => setShowAddHero(false)}
        />
      )}

      {showPicker && (
        <div className="modal-overlay" onClick={() => setShowPicker(false)}>
          <div className="modal-card add-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-tabs">
              <span className="modal-tab active" style={{ cursor: 'default' }}>Противники з джерел</span>
              <div className="modal-tabs-spacer" />
              <button type="button" className="modal-close" onClick={() => setShowPicker(false)} title="Закрити">×</button>
            </div>
            <div className="modal-body">
              <SourcePicker
                onAdd={(payload) => {
                  send({ type: 'combatant:upsert', payload: { ...payload, encounterId: id } });
                  setShowPicker(false);
                }}
                onClose={() => setShowPicker(false)}
              />
            </div>
          </div>
        </div>
      )}

      {diceConfig && (
        <div className="modal-overlay" onClick={() => setDiceConfig(null)}>
          <div className="modal-card" style={{ padding: 24, width: 'min(560px, 92vw)' }} onClick={(e) => e.stopPropagation()}>
            {diceConfig.kind === 'd20' ? (
              <D20Roller
                label={diceConfig.label}
                initialModifier={diceConfig.modifier}
                autoRoll={diceConfig.autoRoll}
                playback={diceConfig.playback}
                onClose={() => setDiceConfig(null)}
                onRollComplete={(result: D20RollResult) => {
                  if (diceConfig.autoRoll && !diceConfig.playback) {
                    const advantage = result.dice.length === 2
                      ? (result.dice[result.pickedIndex] === Math.max(...result.dice) ? 'advantage' : 'disadvantage')
                      : 'none';
                    send({
                      type: 'dice:roll',
                      payload: {
                        kind: 'd20',
                        modifier: result.modifier,
                        advantage,
                        result: { dice: result.dice, pickedIndex: result.pickedIndex },
                        label: diceConfig.label,
                      },
                    });
                  }
                }}
              />
            ) : (
              <DiceRoller
                label={diceConfig.label}
                initialModifier={diceConfig.modifier}
                autoRoll={diceConfig.autoRoll}
                playback={diceConfig.playback}
                onClose={() => setDiceConfig(null)}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Tracker row ──────────────────────────────────────────────────────────
function TrackerRow({ c, expanded, onToggleExpand, onMark, onMax, onCondition, onAddEffect, onRemoveEffect, onDelete, onAttackRoll, onDamageRoll }: {
  c: Combatant;
  expanded: boolean;
  onToggleExpand: () => void;
  onMark: (c: Combatant, field: 'hp' | 'stress' | 'armor', value: number) => void;
  onMax: (c: Combatant, field: 'hpMax' | 'stressMax' | 'armorMax' | 'difficulty' | 'attackMod', value: number) => void;
  onCondition: (c: Combatant, type: string, on: boolean) => void;
  onAddEffect: (c: Combatant, text: string) => void;
  onRemoveEffect: (c: Combatant, effId: string) => void;
  onDelete: (c: Combatant) => void;
  onAttackRoll: (c: Combatant, label: string) => void;
  onDamageRoll: (c: Combatant, formula: string, label: string, damageType?: string) => void;
}): JSX.Element {
  const [effDraft, setEffDraft] = useState('');
  const [editStats, setEditStats] = useState(false);
  const s = c.stats;
  const initial = (c.name?.[0] ?? '?').toUpperCase();
  const hpHint = s.hp >= s.hpMax ? 'ПОВАЛЕНИЙ' : s.hp >= Math.ceil(s.hpMax * 2 / 3) ? 'критично' : s.hp >= Math.ceil(s.hpMax / 3) ? 'поранений' : 'цілий';
  const hasDetail = (s.attacks?.length ?? 0) > 0 || (s.features?.length ?? 0) > 0 || !!s.tactics || !!s.motivation;

  return (
    <div className={`tracker-row ${c.kind}`}>
      <div className="tr-portrait" style={{ background: c.tokenColor ? `radial-gradient(circle at 35% 30%, ${c.tokenColor}, rgba(0,0,0,0.4) 90%)` : undefined }}>
        {c.illustration ? (
          <img src={c.illustration} alt="" />
        ) : c.icon ? (
          <Icon type={iconTypeFor(c.icon)} size={28} stroke={1.6} color="rgba(20,22,15,0.9)" />
        ) : initial}
      </div>

      <div className="tr-main">
        <div className="tr-name-row">
          <button type="button" className="tr-expand" onClick={onToggleExpand} title={expanded ? 'Згорнути' : 'Розгорнути картку'}>
            {expanded ? '▾' : '▸'}
          </button>
          <span className="tr-name" onClick={onToggleExpand} style={{ cursor: 'pointer' }}>{c.name}</span>
          {s.difficulty !== undefined && <span className="tr-diff" title="Складність (Difficulty)">DC {s.difficulty}</span>}
          {s.attackMod !== undefined && <span className="tr-atk" title="Бонус атаки">атк {s.attackMod >= 0 ? '+' : ''}{s.attackMod}</span>}
          <button type="button" className="tr-edit-toggle" onClick={() => setEditStats((v) => !v)} title="Редагувати макс. значення/DC">
            {editStats ? 'готово' : 'ред.'}
          </button>
          <button type="button" className="tr-del" onClick={() => onDelete(c)} title="Прибрати з бою">×</button>
        </div>

        {/* Stat-bars */}
        <div className="tr-bars">
          <StatBar label="HP" hint={hpHint} marked={s.hp} max={s.hpMax} kind="hp"
            onMark={(v) => onMark(c, 'hp', v)} />
          <StatBar label="Стрес" hint={`${s.stress}/${s.stressMax}`} marked={s.stress} max={s.stressMax} kind="stress"
            onMark={(v) => onMark(c, 'stress', v)} />
          {s.armorMax > 0 && (
            <StatBar label="Броня" hint={`${s.armorMax - s.armor} вільн.`} marked={s.armor} max={s.armorMax} kind="armor"
              onMark={(v) => onMark(c, 'armor', v)} />
          )}
        </div>

        {editStats && (
          <div className="tr-edit-grid">
            <NumField label="HP max" value={s.hpMax} min={1} max={30} onChange={(v) => onMax(c, 'hpMax', v)} />
            <NumField label="Стрес max" value={s.stressMax} min={0} max={20} onChange={(v) => onMax(c, 'stressMax', v)} />
            <NumField label="Броня max" value={s.armorMax} min={0} max={12} onChange={(v) => onMax(c, 'armorMax', v)} />
            <NumField label="Складність" value={s.difficulty ?? 0} min={0} max={30} onChange={(v) => onMax(c, 'difficulty', v)} />
            <NumField label="Атака" value={s.attackMod ?? 0} min={-5} max={15} onChange={(v) => onMax(c, 'attackMod', v)} />
          </div>
        )}

        {/* Conditions */}
        <div className="tr-conds">
          {CONDITIONS.map((def) => {
            const on = c.conditions.includes(def.type);
            return (
              <button
                key={def.type}
                type="button"
                className={`tr-cond ${on ? 'on' : ''}`}
                style={{ ['--cd-color' as string]: def.color }}
                onClick={() => onCondition(c, def.type, !on)}
                title={def.label}
              >
                <span className="tr-cond-dot" />{def.short}
              </button>
            );
          })}
        </div>

        {/* Temp effects */}
        <div className="tr-effects">
          {(s.tempEffects ?? []).map((e) => (
            <span key={e.id} className="tr-eff-chip">
              {e.text}
              <button type="button" onClick={() => onRemoveEffect(c, e.id)} title="Прибрати">×</button>
            </span>
          ))}
          <input
            className="tr-eff-input"
            value={effDraft}
            onChange={(ev) => setEffDraft(ev.target.value)}
            onKeyDown={(ev) => {
              if (ev.key === 'Enter' && effDraft.trim()) { onAddEffect(c, effDraft); setEffDraft(''); }
            }}
            placeholder="+ тимчасовий ефект (Enter)"
          />
        </div>

        {/* Розгорнута картка у стилі Daggerheart стат-блоку */}
        {expanded && (
          <div className="dh-sb">
            {(s.tier || s.dhType) && (
              <p className="dh-sb-tier">Тір {s.tier ?? '?'}{s.dhType ? ` · ${s.dhType}` : ''}</p>
            )}
            {!hasDetail && <p className="dh-sb-empty">Нема даних про атаки/здібності.</p>}

            {(s.motivation || s.tactics) && (
              <p className="dh-sb-motives">
                <strong>Мотиви й тактика:</strong> {[s.motivation, s.tactics].filter(Boolean).join(' · ')}
              </p>
            )}

            {/* Стат-смуга */}
            <div className="dh-sb-strip">
              <div className="dh-sb-row">
                <span><strong>Складність:</strong> {s.difficulty ?? '—'}</span>
                <span><strong>Пороги:</strong> {s.thresholdMajor !== undefined ? `${s.thresholdMajor}/${s.thresholdSevere ?? '—'}` : 'Немає'}</span>
                <span><strong>HP:</strong> {s.hpMax}</span>
                <span><strong>Стрес:</strong> {s.stressMax}</span>
              </div>
              {s.attacks.map((a, i) => (
                <div key={i} className="dh-sb-row atk">
                  <span><strong>АТК:</strong> {(s.attackMod ?? 0) >= 0 ? '+' : ''}{s.attackMod ?? 0}</span>
                  <span><strong>{a.name}:</strong> {a.range ?? 'Melee'}</span>
                  <span className="dh-sb-dmg">{a.damage}{a.damageType ? ` ${a.damageType}` : ''}</span>
                  <button type="button" className="dh-sb-roll" onClick={() => onAttackRoll(c, `${c.name}: ${a.name}`)}
                    title={`d20${s.attackMod ? (s.attackMod > 0 ? '+' : '') + s.attackMod : ''} на влучання`}>Атк</button>
                  {a.damage && (
                    <button type="button" className="dh-sb-roll dmg" onClick={() => onDamageRoll(c, a.damage, `${c.name}: ${a.name}`, a.damageType)}
                      title={`Кидок шкоди ${a.damage}`}>шкода</button>
                  )}
                </div>
              ))}
            </div>

            {(s.features?.length ?? 0) > 0 && (
              <>
                <div className="dh-sb-h">Особливості</div>
                {s.features.map((f, i) => (
                  <p key={i} className="dh-sb-feat">
                    <span className="dh-sb-feat-name">{f.name}</span>
                    {f.description ? <> {f.description}</> : null}
                  </p>
                ))}
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatBar({ label, hint, marked, max, kind, onMark }: {
  label: string;
  hint: string;
  marked: number;
  max: number;
  kind: 'hp' | 'stress' | 'armor';
  onMark: (value: number) => void;
}): JSX.Element {
  return (
    <div className="tr-bar">
      <div className="tr-bar-head">
        <span className="tr-bar-label">{label}</span>
        <span className="tr-bar-hint">{marked}/{max} · {hint}</span>
      </div>
      <div className="tr-bar-controls">
        <button type="button" className="tr-step" onClick={() => onMark(marked - 1)} disabled={marked <= 0} title="−1">−</button>
        <PipBar marked={marked} max={max} kind={kind} onToggle={(i, was) => onMark(nextMarks(marked, i, was))} />
        <button type="button" className="tr-step" onClick={() => onMark(marked + 1)} disabled={marked >= max} title="+1">+</button>
      </div>
    </div>
  );
}

function NumField({ label, value, min, max, onChange }: {
  label: string; value: number; min: number; max: number; onChange: (v: number) => void;
}): JSX.Element {
  return (
    <label className="tr-num">
      <span>{label}</span>
      <input
        type="number"
        value={value}
        min={min}
        max={max}
        onChange={(e) => {
          const v = Number(e.target.value);
          if (Number.isFinite(v)) onChange(Math.max(min, Math.min(max, v)));
        }}
      />
    </label>
  );
}

// ── Battle Points (калькулятор складності бою) ───────────────────────────
function BattlePoints({ foes, defaultPcs }: { foes: Combatant[]; defaultPcs: number }): JSX.Element {
  const [pcs, setPcs] = useState(defaultPcs);
  // Дискреційні коригування бюджету (з офіційного Battle Guide).
  const [adj, setAdj] = useState<Set<string>>(new Set());
  const tgl = (k: string): void => setAdj((s) => { const n = new Set(s); n.has(k) ? n.delete(k) : n.add(k); return n; });

  const ADJ: Array<{ k: string; label: string; delta: number; title: string }> = [
    { k: 'easy', label: 'Легший −1', delta: -1, title: 'Бій має бути коротшим/легшим' },
    { k: 'solos', label: '2+ Solo −2', delta: -2, title: '2 або більше Solo-ворогів' },
    { k: 'dmg', label: '+шкода всім −2', delta: -2, title: '+1d4 (або +2) до шкоди всіх ворогів' },
    { k: 'lowtier', label: 'Нижчий тір +1', delta: +1, title: 'Узято ворога з нижчого тіру' },
    { k: 'nobig', label: 'Без важких +1', delta: +1, title: 'Немає Bruiser/Horde/Leader/Solo' },
    { k: 'hard', label: 'Небезпечніший +2', delta: +2, title: 'Бій має бути довшим/небезпечнішим' },
  ];

  const base = 3 * Math.max(0, pcs) + 2;
  const budget = base + ADJ.reduce((sum, a) => sum + (adj.has(a.k) ? a.delta : 0), 0);

  // Вартість витрачених очок за типами ворогів.
  let spent = 0;
  let minions = 0;
  for (const f of foes) {
    const t = f.stats.dhType;
    if (t === 'Minion') { minions++; continue; }
    if (t === 'Social' || t === 'Support') spent += 1;
    else if (t === 'Leader') spent += 3;
    else if (t === 'Bruiser') spent += 4;
    else if (t === 'Solo') spent += 5;
    else spent += 2; // Horde/Ranged/Skulk/Standard (та невідомі)
  }
  if (minions > 0 && pcs > 0) spent += Math.ceil(minions / pcs); // 1 очко за групу мінйонів = розмір партії

  const remaining = budget - spent;

  return (
    <div className="battle-points">
      <div className="bp-pcs">
        <label htmlFor="bp-pcs">Гравців у бою:</label>
        <input id="bp-pcs" type="number" min={1} max={8} value={pcs}
          onChange={(e) => setPcs(Math.max(1, Math.min(8, Number(e.target.value) || 1)))} />
      </div>
      <div className="bp-budget">
        <span>Бюджет:</span><span className="bp-num">{budget}</span>
        <span>· Витрачено:</span><span className="bp-num">{spent}</span>
        <span>· Залишок:</span>
        <span className={`bp-num ${remaining >= 0 ? 'ok' : 'over'}`}>{remaining}</span>
      </div>
      <div className="bp-adjust">
        {ADJ.map((a) => (
          <button key={a.k} type="button" className={`bp-chip ${adj.has(a.k) ? 'on' : ''}`}
            title={a.title} onClick={() => tgl(a.k)}>{a.label}</button>
        ))}
      </div>
      <span className="bp-hint">формула: 3 × гравців + 2</span>
    </div>
  );
}

// ── Track fear (compact) ─────────────────────────────────────────────────
function TrackFear({ value, onAdjust }: { value: number; onAdjust: (d: number) => void }): JSX.Element {
  return (
    <div className="tracker-fear">
      <span className="tf-label">Страх</span>
      <button type="button" onClick={() => onAdjust(-1)} disabled={value <= 0}>−</button>
      <span className="tf-value">{value}/12</span>
      <button type="button" onClick={() => onAdjust(1)} disabled={value >= 12}>+</button>
    </div>
  );
}

// ── Add hero modal ───────────────────────────────────────────────────────
function AddHeroModal({ onSubmit, onCancel }: {
  onSubmit: (payload: Partial<Combatant>) => void;
  onCancel: () => void;
}): JSX.Element {
  const [name, setName] = useState('');
  const [hp, setHp] = useState(6);
  const [stress, setStress] = useState(6);
  const [armor, setArmor] = useState(3);
  const [difficulty, setDifficulty] = useState(0);
  const [kind, setKind] = useState<'player' | 'ally'>('player');
  const [color, setColor] = useState('#cca766');

  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => { if (e.key === 'Escape') onCancel(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  const submit = (): void => {
    if (!name.trim()) return;
    onSubmit({
      name: name.trim(),
      kind,
      // Daggerheart marks: стартуємо з 0 (здоровий).
      stats: {
        difficulty: difficulty > 0 ? difficulty : undefined,
        hpMax: hp, hp: 0,
        stressMax: stress, stress: 0,
        armorMax: armor, armor: 0,
        attacks: [], features: [],
      },
      conditions: [],
      tokenX: 5, tokenY: 5,
      tokenColor: color,
      hidden: false,
      isMinionGroup: false,
      count: 1,
    });
  };

  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal-card" style={{ width: 'min(440px, 92vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-tabs">
          <span className="modal-tab active" style={{ cursor: 'default' }}>Новий герой</span>
          <div className="modal-tabs-spacer" />
          <button type="button" className="modal-close" onClick={onCancel}>×</button>
        </div>
        <div className="modal-body">
          <div className="hero-form">
            <label className="hero-field full">
              <span>Імʼя</span>
              <input value={name} autoFocus onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') submit(); }} placeholder="Аделіна" />
            </label>
            <label className="hero-field">
              <span>Тип</span>
              <select value={kind} onChange={(e) => setKind(e.target.value as 'player' | 'ally')}>
                <option value="player">Гравець</option>
                <option value="ally">Союзник</option>
              </select>
            </label>
            <label className="hero-field">
              <span>Колір</span>
              <input type="color" value={color} onChange={(e) => setColor(e.target.value)} />
            </label>
            <label className="hero-field"><span>HP</span>
              <input type="number" min={1} max={30} value={hp} onChange={(e) => setHp(Number(e.target.value) || 1)} /></label>
            <label className="hero-field"><span>Стрес</span>
              <input type="number" min={0} max={20} value={stress} onChange={(e) => setStress(Number(e.target.value) || 0)} /></label>
            <label className="hero-field"><span>Броня</span>
              <input type="number" min={0} max={12} value={armor} onChange={(e) => setArmor(Number(e.target.value) || 0)} /></label>
            <label className="hero-field"><span>Складність</span>
              <input type="number" min={0} max={30} value={difficulty} onChange={(e) => setDifficulty(Number(e.target.value) || 0)} /></label>
          </div>
          <div className="hero-actions">
            <button type="button" className="hero-submit" onClick={submit}>Додати героя</button>
            <button type="button" className="hero-cancel" onClick={onCancel}>Скасувати</button>
          </div>
        </div>
      </div>
    </div>
  );
}
